/**
 * The account, as opposed to the run.
 *
 * A run is one dive: where you are, what is left in the rack, which crates have
 * moved, how many hulls you have left. It lives in `abyss.save.v2` and it is
 * thrown away when you start a new one.
 *
 * A profile is everything you *own*: the boats, the kit bolted to them, banked
 * credits, and what you have seen. It survives NEW DIVE, and it is what makes
 * more than one mode worth having — earning a hull in Survival and flying it in
 * the campaign is the entire point of the PRD's shared fleet, and it cannot work
 * while ownership is a field on the run.
 *
 * **`abyss.save.v2` is read here and never written and never deleted.** A
 * botched split has to be recoverable, and `loadSave` fails silently by design —
 * it returns null on a version mismatch, which surfaces only as a missing
 * CONTINUE row. Leaving the old key intact means a bad migration costs a
 * bug report rather than somebody's 39 minutes.
 *
 * Deliberately NOT in here: salvage spent during a campaign dive. That stays
 * run-scoped, because the campaign's economy is tuned around it and moving it
 * would re-balance a game somebody has just finished. Survival banks its
 * earnings into `credits`; the campaign banks nothing and behaves exactly as it
 * did.
 */

import { emptyLevels, type Levels } from './upgrades';
import { loadSave } from './save';

const PKEY = 'abyss.profile.v1';
const PVERSION = 1;

export interface OwnedCraft {
  id: string;
  /** Structural modules — hull, shield, engine. They belong to this boat. */
  levels: Levels;
}

export interface Profile {
  v: number;
  /** Banked. Spans every mode. */
  credits: number;
  fleet: OwnedCraft[];
  /** Kit — cannon, torpedo, sonar, lamp. Refits across the whole fleet. */
  equipment: Levels;
  hulls: string[];
  active: string;
  /** POIs found, so a discovery is a discovery once. */
  discoveries: string[];
  /** Onboarding lines already shown; they should not repeat in a new dive. */
  hintsSeen: string[];
  stats: {
    dives: number;
    kills: number;
    /** Metres, positive. */
    deepest: number;
    playTime: number;
    lifetimeSalvage: number;
    /** Best score per mode. Survival's leaderboard, locally. */
    best: Record<string, number>;
  };
}

export function blankProfile(): Profile {
  return {
    v: PVERSION,
    credits: 0,
    fleet: [{ id: 'pathfinder', levels: emptyLevels() }],
    equipment: emptyLevels(),
    hulls: ['pathfinder'],
    active: 'pathfinder',
    discoveries: [],
    hintsSeen: [],
    stats: { dives: 0, kills: 0, deepest: 0, playTime: 0, lifetimeSalvage: 0, best: {} },
  };
}

function storage(): Storage | null {
  try {
    // Safari in private mode throws on access, not on use — same guard save.ts uses.
    const s = window.localStorage;
    s.getItem(PKEY);
    return s;
  } catch {
    return null;
  }
}

/**
 * Lift ownership out of an existing run save. Called once, when there is a save
 * but no profile — i.e. for everybody who was already playing.
 */
function migrateFromSave(): Profile | null {
  const old = loadSave();
  if (!old) return null;

  const p = blankProfile();
  try {
    p.equipment = { ...emptyLevels(), ...(old.equipment ?? {}) };
    if (old.fleet?.length) {
      p.fleet = old.fleet.map((f) => ({ id: f.id, levels: { ...emptyLevels(), ...f.levels } }));
    } else {
      // Older still: one flat `levels`, from before hulls existed. Structure
      // belongs to the boat everyone was flying; kit becomes kit.
      const l = { ...emptyLevels(), ...(old.levels ?? {}) };
      p.fleet = [{ id: 'pathfinder', levels: { ...emptyLevels(), hull: l.hull, shield: l.shield, engine: l.engine } }];
      p.equipment = { ...emptyLevels(), cannon: l.cannon, torpedo: l.torpedo, sonar: l.sonar, lamp: l.lamp };
    }
    p.hulls = old.hulls?.length ? [...old.hulls] : ['pathfinder'];
    if (!p.hulls.includes('pathfinder')) p.hulls.push('pathfinder');
    p.active = old.active || 'pathfinder';
    p.discoveries = [...(old.seenPois ?? [])];
    p.hintsSeen = [...(old.hints ?? [])];
    p.stats.kills = old.kills ?? 0;
    p.stats.playTime = old.playTime ?? 0;
    // `reachedX` is a distance along the world, not a depth, so there is
    // nothing here to seed `deepest` from honestly. It starts at 0 and the
    // first dive fills it in.
    console.info('[abyss] migrated a profile out of abyss.save.v2');
    return p;
  } catch (err) {
    // Loudly, and then fall back — but the old key is untouched either way, so
    // this is recoverable rather than a lost account.
    console.error('[abyss] profile migration failed; abyss.save.v2 is intact', err);
    return null;
  }
}

export function loadProfile(): Profile {
  const s = storage();
  if (!s) return blankProfile();

  const raw = s.getItem(PKEY);
  if (raw) {
    try {
      const data = JSON.parse(raw) as Partial<Profile>;
      if (data.v === PVERSION) {
        const out = { ...blankProfile(), ...data };
        out.equipment = { ...emptyLevels(), ...(data.equipment ?? {}) };
        out.fleet = (data.fleet ?? []).map((f) => ({ id: f.id, levels: { ...emptyLevels(), ...f.levels } }));
        if (!out.fleet.length) out.fleet = blankProfile().fleet;
        out.stats = { ...blankProfile().stats, ...(data.stats ?? {}) };
        return out;
      }
      console.warn(`[abyss] profile version ${data.v} is not ${PVERSION}; starting fresh`);
    } catch (err) {
      console.error('[abyss] profile unreadable; starting fresh', err);
    }
  }

  const migrated = migrateFromSave();
  if (migrated) {
    writeProfile(migrated);
    return migrated;
  }
  return blankProfile();
}

export function writeProfile(p: Profile) {
  try {
    storage()?.setItem(PKEY, JSON.stringify(p));
  } catch {
    // Quota or private mode. A game that cannot save is still a game.
  }
}

/** Bank a run's earnings. The one way credits ever go up. */
export function bank(p: Profile, credits: number, mode: string, score: number) {
  p.credits += Math.max(0, Math.round(credits));
  p.stats.lifetimeSalvage += Math.max(0, Math.round(credits));
  p.stats.dives++;
  if (score > (p.stats.best[mode] ?? 0)) p.stats.best[mode] = score;
  writeProfile(p);
}
